import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import Header from "../../../../components/ui/ListHeader";
import {
  ADMIN_CANCEL_REPORT_JOB,
  ADMIN_RETRY_REPORT_JOB,
} from "../../../../graphql/mutation/ReportJob";
import { FETCH_ADMIN_REPORT_JOBS } from "../../../../graphql/query/ReportJob";
import {
  formatReportJobProgress,
  hasActiveReportJobs,
  REPORT_JOB_STATUS_LABELS,
  REPORT_JOB_STATUS_STYLES,
  reportJobLabel,
} from "../../../Reporting/utils";
import {
  adminReportJobInput,
  canAdminCancelReportJob,
  canAdminRetryReportJob,
  reportJobAge,
  reportJobLastActivity,
} from "./utils";

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 4000;

const StatusBadge = ({ job }) => (
  <span
    className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
      REPORT_JOB_STATUS_STYLES[job?.status] || "bg-gray-100 text-gray-700"
    }`}
  >
    {REPORT_JOB_STATUS_LABELS[job?.status] || job?.status}
    {formatReportJobProgress(job) ? ` (${formatReportJobProgress(job)})` : ""}
  </span>
);

const mutationError = (result, fallback) => {
  const error = result?.error;
  if (Array.isArray(error)) return error.join(", ");
  return error || fallback;
};

const ReportJobs = () => {
  const [filters, setFilters] = useState({
    statusFilter: "active",
    report: "",
    correlationId: "",
    userId: "",
    createdFrom: "",
    createdTo: "",
    offset: 0,
  });
  const [actionJobId, setActionJobId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const input = adminReportJobInput({
    ...filters,
    limit: PAGE_SIZE,
  });
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    FETCH_ADMIN_REPORT_JOBS,
    {
      variables: { input },
      fetchPolicy: "cache-and-network",
    },
  );
  const [cancelReportJob] = useMutation(ADMIN_CANCEL_REPORT_JOB);
  const [retryReportJob] = useMutation(ADMIN_RETRY_REPORT_JOB);

  const jobs = data?.adminReportJobs?.edges || [];
  const count = data?.adminReportJobs?.count || 0;
  const active = hasActiveReportJobs(jobs);
  const page = Math.floor(filters.offset / PAGE_SIZE);
  const pageCount = Math.max(Math.ceil(count / PAGE_SIZE), 1);

  useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [active, startPolling, stopPolling]);

  const updateFilter = event => {
    const { name, value } = event.target;
    setFilters(current => ({ ...current, [name]: value, offset: 0 }));
  };

  const handleCancel = async job => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Cancel report job ${job.id}?`)) return;
    setActionJobId(job.id);
    setActionError(null);
    try {
      const { data: mutationData } = await cancelReportJob({
        variables: { id: job.id },
      });
      const result = mutationData?.adminCancelReportJob;
      if (!result?.success) {
        throw new Error(mutationError(result, "Failed to cancel the report"));
      }
      await refetch();
    } catch (mutationFailure) {
      setActionError(mutationFailure.message || "Failed to cancel the report");
    } finally {
      setActionJobId(null);
    }
  };

  const handleRetry = async job => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Retry report job ${job.id}?`)) return;
    setActionJobId(job.id);
    setActionError(null);
    try {
      const { data: mutationData } = await retryReportJob({
        variables: { id: job.id },
      });
      const result = mutationData?.adminRetryReportJob;
      if (!result?.success) {
        throw new Error(mutationError(result, "Failed to retry the report"));
      }
      await refetch();
    } catch (mutationFailure) {
      setActionError(mutationFailure.message || "Failed to retry the report");
    } finally {
      setActionJobId(null);
    }
  };

  return (
    <>
      <Header
        leftText="Report jobs"
        rightComponent={() => (
          <span className="text-sm text-gray-500">
            {count} matching job{count === 1 ? "" : "s"}
          </span>
        )}
      />
      <main className="px-4 pb-8 sm:px-6 lg:px-8">
        <div className="my-6 grid grid-cols-1 gap-4 rounded-md border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 xl:grid-cols-6">
          <label className="text-xs font-medium uppercase text-gray-500">
            Status
            <select
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="statusFilter"
              value={filters.statusFilter}
              onChange={updateFilter}
            >
              <option value="active">Active</option>
              <option value="stuck">Stuck</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="complete">Complete</option>
              <option value="all">All</option>
            </select>
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            Report
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="report"
              value={filters.report}
              onChange={updateFilter}
              placeholder="brochure"
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            Correlation ID
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="correlationId"
              value={filters.correlationId}
              onChange={updateFilter}
              placeholder="UUID or prefix"
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            User ID
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              min="1"
              name="userId"
              type="number"
              value={filters.userId}
              onChange={updateFilter}
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            From
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="createdFrom"
              type="date"
              value={filters.createdFrom}
              onChange={updateFilter}
            />
          </label>
          <label className="text-xs font-medium uppercase text-gray-500">
            To
            <input
              className="mt-1 block w-full rounded border-gray-300 bg-white text-sm"
              name="createdTo"
              type="date"
              value={filters.createdTo}
              onChange={updateFilter}
            />
          </label>
        </div>

        {error ? (
          <div className="mb-4 text-sm text-red-600">
            Failed to load report jobs: {error.message}
          </div>
        ) : null}
        {actionError ? (
          <div className="mb-4 text-sm text-red-600">{actionError}</div>
        ) : null}
        {loading && jobs.length === 0 ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : null}
        {!loading && jobs.length === 0 && !error ? (
          <div className="text-sm text-gray-500">
            No report jobs match these filters.
          </div>
        ) : null}

        {jobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Job</th>
                  <th className="px-4 py-2">Requested by</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Age</th>
                  <th className="px-4 py-2">Last activity</th>
                  <th className="py-2 pl-4" />
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} className="border-b border-gray-100 text-sm">
                    <td className="py-3 pr-4 align-top">
                      <div className="font-medium text-gray-900">
                        #{job.id} · {reportJobLabel(job)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {job.type} · {job.format?.toUpperCase()} · Attempt{" "}
                        {job.attempts || 0}
                      </div>
                      <button
                        type="button"
                        className="mt-1 font-mono text-xs text-blue-600 hover:text-blue-800"
                        title="Copy correlation ID"
                        onClick={() =>
                          navigator.clipboard?.writeText(job.correlationId)
                        }
                      >
                        {job.correlationId}
                      </button>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div>{job.owner?.name || `User ${job.userId}`}</div>
                      <div className="text-xs text-gray-500">
                        {job.owner?.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge job={job} />
                      {job.error ? (
                        <div className="mt-1 max-w-md break-words text-xs text-red-600">
                          {job.error}
                        </div>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
                      {reportJobAge(job)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 align-top text-gray-500">
                      {reportJobLastActivity(job)
                        ? new Date(reportJobLastActivity(job)).toLocaleString()
                        : "—"}
                    </td>
                    <td className="py-3 pl-4 text-right align-top">
                      <div className="flex justify-end gap-3">
                        {job.format === "pdf" && job.downloadUrl ? (
                          <a
                            className="whitespace-nowrap font-medium text-blue-600 hover:text-blue-800"
                            href={job.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View PDF
                          </a>
                        ) : null}
                        {canAdminCancelReportJob(job) ? (
                          <button
                            type="button"
                            className="font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                            disabled={actionJobId === job.id}
                            onClick={() => handleCancel(job)}
                          >
                            Cancel
                          </button>
                        ) : null}
                        {canAdminRetryReportJob(job) ? (
                          <button
                            type="button"
                            className="font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            disabled={actionJobId === job.id}
                            onClick={() => handleRetry(job)}
                          >
                            Retry
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {jobs.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-gray-500">
              {filters.offset + 1}–
              {Math.min(filters.offset + jobs.length, count)} of {count}
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="font-medium text-blue-600 disabled:text-gray-300"
                disabled={page === 0}
                onClick={() =>
                  setFilters(current => ({
                    ...current,
                    offset: Math.max(current.offset - PAGE_SIZE, 0),
                  }))
                }
              >
                Previous
              </button>
              <span className="text-gray-500">
                Page {page + 1} of {pageCount}
              </span>
              <button
                type="button"
                className="font-medium text-blue-600 disabled:text-gray-300"
                disabled={page >= pageCount - 1}
                onClick={() =>
                  setFilters(current => ({
                    ...current,
                    offset: current.offset + PAGE_SIZE,
                  }))
                }
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </>
  );
};

export default ReportJobs;
